/* eslint-disable import/prefer-default-export */
import startOfMonth from 'date-fns/startOfMonth';
import add from 'date-fns/add';
import format from 'date-fns/format';
import startOfDay from 'date-fns/startOfDay';
import sub from 'date-fns/sub';
import { DateString } from '../types';
import captureException from './captureException';

const DATE_FNS_FORMATS = {
  'YYYY-MM-DD': 'yyyy-MM-dd',
  /** Full month name */
  MMMM: 'MMMM',
  /** short month name */
  MMM: 'MMM',
  YYYY: 'yyyy',
} as const;

type DateFnsFormats = keyof typeof DATE_FNS_FORMATS;

/**
 * Formats a date object using `date-fns`.
 * Defaults to `YYYY-MM-DD`.
 */
export const formatDate = (
  date: Date,
  formatString: DateFnsFormats = 'YYYY-MM-DD',
) => format(date, DATE_FNS_FORMATS[formatString]);

/**
 * This should only be used with dates that are constructed with a date string
 */
const adjustForTimezones = (date: Date): Date => new Date(
  date.getTime() + (date.getTimezoneOffset() * 60000),
);

/**
 * Returns a Date that has been adjusted for timezones.
 */
export const newTZDate = (date: DateString) => adjustForTimezones(new Date(date));

/**
 * Returns a Date representing the start of today.
 */
export const todayDate = () => startOfDay(new Date());

/**
 * Returns a Date representing the start of tomorrow.
 */
export const tomorrowDate = () => add(todayDate(), { days: 1 });

/**
 * Returns a Date representing the start of month.
 */
export const startDateOfMonth = (month: Date) => startOfMonth(month);

/**
 * Adds 1 month to the specified month.
 */
export const addMonth = (month: DateString): DateString => (
  formatDate(add(newTZDate(month), { months: 1 }))
);

/**
 * Subtracts 1 month to the specified month.
 */
export const subMonth = (month: DateString): DateString => (
  formatDate(sub(newTZDate(month), { months: 1 }))
);

/**
 *
 * Returns an object representing the duration of a timespan.
 */
export const convertTimeSpanToDuration = (value: string): Duration => {
  const regex = /(\d+|)(?:\.|\s|)(\d\d:\d\d:\d\d)/;
  try {
    if (!regex.test(value)) throw new Error(`Invalid timespan: ${value}`);
  } catch (err) {
    captureException(err, null);
  }
  const [, days, time] = regex.exec(value) as string[];
  const [hours, minutes] = time.split(':');
  return {
    days: parseInt(days || '0', 10),
    hours: parseInt(hours, 10),
    minutes: parseInt(minutes, 10),
  };
};
